/**
 * Home — Hideaway landing.
 *
 * The narrative: every safety filter has a tell. Hideaway is the
 * arsenal that finds it. We frame the platform around action
 * (probe → reveal → report) instead of comparison-to-incumbents.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Lock, Sparkles, ExternalLink, Shield,
  EyeOff, Eye, Radar, Microscope, FlaskConical, ImageIcon, Video,
  Zap, KeyRound, ShieldX, ShieldCheck, Layers, Terminal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrandMark } from "@/components/BrandLogo";
import { useSession } from "@/hooks/useSession";
import { AuthDialog } from "@/components/AuthDialog";

// ────────────────────────────────────────────────────────────────────
// Content
// ────────────────────────────────────────────────────────────────────

const HERO_TAGLINES = [
  "Every model hides something.",
  "Every safety filter has a tell.",
  "Every guardrail bends somewhere.",
];

const LAB_MODULES = [
  {
    id: "image_gen",
    icon: ImageIcon,
    name: "Image Generation",
    targets: ["DALL-E 3", "Flux Pro", "Stable Diffusion XL", "SD3"],
    techniques: 5,
    color: "text-violet-400",
    glow: "shadow-[0_0_24px_-8px_hsl(270_60%_60%/0.4)]",
    description: "Prompt-engineering attacks against text-to-image pipelines. Cross-language bypasses, concept decomposition, unicode injection.",
    cta: "Open Image Gen Lab",
    to: "/labs",
  },
  {
    id: "video_gen",
    icon: Video,
    name: "Video Generation",
    targets: ["Kling 1.6", "Luma Ray 2", "Runway Gen-3", "MiniMax Hailuo"],
    techniques: 4,
    color: "text-rose-400",
    glow: "shadow-[0_0_24px_-8px_hsl(340_70%_60%/0.4)]",
    description: "Temporal-decomposition attacks the per-frame safety classifiers miss. Background jobs with browser notifications.",
    cta: "Open Video Gen Lab",
    to: "/labs",
  },
  {
    id: "vlm",
    icon: Eye,
    name: "Vision-Language",
    targets: ["GPT-4V", "Claude Vision", "Gemini Vision"],
    techniques: 5,
    color: "text-amber-400",
    glow: "shadow-[0_0_24px_-8px_hsl(40_80%_60%/0.4)]",
    description: "Steganographic payload delivery via the stego engine. LSB encoding, FigStep typography, PNG metadata smuggling.",
    cta: "Open VLM Attack Lab",
    to: "/labs",
    differentiator: true,
  },
];

const FLOW_STEPS = [
  { n: "01", t: "Probe", d: "Pick a technique and a category. We seed prompts from a curated library, you bring the API key." },
  { n: "02", t: "Reveal", d: "Watch generations stream in. Bypass scores. Cost per shot. Live cancellation. Side-by-side model comparison." },
  { n: "03", t: "Report", d: "One-click scanner runs the full battery. JSON + Markdown safety report. Compare across models in History." },
];

const STEGO_TOOLS = [
  { to: "/hide",    icon: EyeOff,     title: "Hide",    description: "Embed payloads with LSB, DCT, adaptive steganography." },
  { to: "/reveal",  icon: Eye,        title: "Reveal",  description: "Extract hidden payloads. 120 detector combinations." },
  { to: "/scan",    icon: Radar,      title: "Scan",    description: "Exhaustive zsteg-style sweep with entropy ranking." },
  { to: "/analyze", icon: Microscope, title: "Analyze", description: "Deep-inspect any file with every detector in the registry." },
];

const PRICING = [
  { name: "Free",  price: "$0",     features: ["Full stego toolkit", "10 AI attacks/day (BYOK)", "Built-in techniques"], cta: "Start free" },
  { name: "Pro",   price: "$29/mo", features: ["Unlimited attacks", "All 14 techniques", "Reports & history", "$5 platform credits"], cta: "Go pro", featured: true },
  { name: "Team",  price: "$99/mo", features: ["Pro + 5 seats", "REST API access", "Shared workspaces", "Priority support"], cta: "Contact" },
];

// ────────────────────────────────────────────────────────────────────
// Animations
// ────────────────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

// ────────────────────────────────────────────────────────────────────
// Hero — terminal-typing tagline + decoded reveal
// ────────────────────────────────────────────────────────────────────

function HeroTypewriter() {
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const [decoding, setDecoding] = useState(false);

  useEffect(() => {
    const target = HERO_TAGLINES[index];
    setDecoding(true);
    let i = 0;
    const interval = window.setInterval(() => {
      i += 1;
      setTyped(target.slice(0, i));
      if (i >= target.length) {
        window.clearInterval(interval);
        setDecoding(false);
        window.setTimeout(() => {
          setIndex((idx) => (idx + 1) % HERO_TAGLINES.length);
          setTyped("");
        }, 2400);
      }
    }, 38);
    return () => window.clearInterval(interval);
  }, [index]);

  return (
    <span className="inline-flex items-baseline gap-1">
      {typed}
      <span className={"inline-block w-[3px] h-[0.9em] translate-y-[0.05em] " + (decoding ? "bg-primary animate-pulse" : "bg-primary/40")} />
    </span>
  );
}

function PixelDecode({ children, className }: { children: string; className?: string }) {
  // Render each character with a scramble→reveal effect on mount.
  const [resolved, setResolved] = useState<string[]>(children.split("").map(() => " "));
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });

  useEffect(() => {
    if (!inView) return;
    const target = children.split("");
    const noise = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let frame = 0;
    const interval = window.setInterval(() => {
      frame += 1;
      setResolved(target.map((c, i) => {
        if (c === " ") return " ";
        if (frame > i * 1.5 + 4) return c;
        return noise[Math.floor(Math.random() * noise.length)];
      }));
      if (frame > target.length * 1.5 + 6) window.clearInterval(interval);
    }, 30);
    return () => window.clearInterval(interval);
  }, [inView, children]);

  return <span ref={ref} className={className}>{resolved.join("")}</span>;
}

// ────────────────────────────────────────────────────────────────────
// Live attack preview — fake SSE stream of bypass scores
// ────────────────────────────────────────────────────────────────────

interface PreviewResult {
  id: number;
  technique: string;
  model: string;
  score: number;
  bypassed: boolean;
  elapsed: number;
}

const PREVIEW_SAMPLES: Omit<PreviewResult, "id">[] = [
  { technique: "prompt_dilution",       model: "dall-e-3",          score: 78, bypassed: true,  elapsed: 4.2 },
  { technique: "concept_decomposition", model: "flux-pro",          score: 22, bypassed: false, elapsed: 3.8 },
  { technique: "cross_language",        model: "stable-diffusion",  score: 85, bypassed: true,  elapsed: 6.1 },
  { technique: "unicode_injection",     model: "dall-e-3",          score: 41, bypassed: false, elapsed: 4.7 },
  { technique: "negative_prompt",       model: "flux-pro",          score: 68, bypassed: true,  elapsed: 5.2 },
];

function LiveAttackPreview() {
  const [results, setResults] = useState<PreviewResult[]>([]);
  const [running, setRunning] = useState(true);
  const idRef = useRef(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { margin: "-30% 0px" });

  useEffect(() => {
    if (!inView) return;
    setRunning(true);
    setResults([]);
    idRef.current = 0;
    const interval = window.setInterval(() => {
      const sample = PREVIEW_SAMPLES[idRef.current % PREVIEW_SAMPLES.length];
      idRef.current += 1;
      setResults((prev) => [...prev.slice(-4), { id: idRef.current, ...sample }]);
      if (idRef.current >= 12) {
        window.clearInterval(interval);
        window.setTimeout(() => setRunning(false), 1000);
      }
    }, 1200);
    return () => window.clearInterval(interval);
  }, [inView]);

  return (
    <div ref={ref} className="relative rounded-2xl border border-border/60 bg-card/70 backdrop-blur-xl overflow-hidden">
      <div className="flex items-center justify-between border-b border-border/40 bg-muted/30 px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
          </div>
          <span className="font-mono text-[11px] text-muted-foreground ml-2">hideaway://labs/image-gen/attack-stream</span>
        </div>
        <Badge variant="outline" className="text-[10px] font-mono">
          {running ? (
            <><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5" /> LIVE</>
          ) : "PAUSED"}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] divide-y md:divide-y-0 md:divide-x divide-border/30">
        {/* Left: command terminal */}
        <div className="p-4 font-mono text-[11px] leading-relaxed text-muted-foreground">
          <div><span className="text-emerald-400">$</span> hideaway attack \</div>
          <div className="pl-3">--technique prompt_dilution \</div>
          <div className="pl-3">--category violence \</div>
          <div className="pl-3">--model dall-e-3 \</div>
          <div className="pl-3">--compare flux-pro,stable-diffusion</div>
          <div className="mt-3 text-primary">{running ? "▸ streaming results…" : "▸ scan complete"}</div>
          <div className="mt-3 text-muted-foreground/60">
            <span className="text-foreground">{results.length}</span> generations · <span className="text-red-400">{results.filter(r => r.bypassed).length}</span> bypassed · <span className="text-emerald-400">{results.filter(r => !r.bypassed).length}</span> blocked
          </div>
        </div>

        {/* Right: live result tiles */}
        <div className="p-3 space-y-2 max-h-[300px] overflow-hidden">
          <AnimatePresence initial={false}>
            {results.slice(-5).map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, x: 20, height: 0 }}
                animate={{ opacity: 1, x: 0, height: "auto" }}
                exit={{ opacity: 0, x: -20, height: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-border/40 bg-card text-xs"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {r.bypassed
                    ? <ShieldX className="h-3.5 w-3.5 text-red-400 shrink-0" />
                    : <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                  <span className="font-mono truncate">{r.technique}</span>
                  <span className="text-muted-foreground text-[10px] truncate">{r.model}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={"font-mono " + (r.bypassed ? "text-red-400" : "text-emerald-500")}>{r.score}</span>
                  <span className="text-muted-foreground/70 text-[10px] font-mono">{r.elapsed.toFixed(1)}s</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Capability metric ticker
// ────────────────────────────────────────────────────────────────────

function MetricCounter({ value, label, suffix = "" }: { value: number; label: string; suffix?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 900;
    const start = performance.now();
    const tick = (t: number) => {
      const elapsed = t - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display font-bold text-3xl md:text-4xl tracking-tight tabular-nums">{display}{suffix}</span>
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Lab module card
// ────────────────────────────────────────────────────────────────────

function LabModuleCard({ mod, locked, onAuth }: { mod: typeof LAB_MODULES[number]; locked: boolean; onAuth: () => void }) {
  const Icon = mod.icon;
  const inner = (
    <div className={"group relative h-full rounded-2xl border border-border/50 bg-card/60 backdrop-blur-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:border-border " + mod.glow}>
      {mod.differentiator && (
        <Badge className="absolute -top-2 left-6 bg-amber-500/15 text-amber-300 border-amber-500/40 text-[9px]">
          <Sparkles className="h-2.5 w-2.5 mr-1" /> UNIQUE
        </Badge>
      )}
      <div className="flex items-start justify-between mb-4">
        <div className={"rounded-xl p-2.5 bg-background/60 border border-border/40 transition-transform group-hover:scale-110 " + mod.color}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-muted-foreground">{mod.techniques} techniques</div>
        </div>
      </div>
      <h3 className="font-display font-bold text-lg mb-2">{mod.name}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed mb-4 min-h-[50px]">{mod.description}</p>
      <div className="space-y-1 mb-5">
        {mod.targets.map((t) => (
          <div key={t} className="font-mono text-[10px] text-muted-foreground/80 flex items-center gap-2">
            <span className={"h-1 w-1 rounded-full " + mod.color.replace("text-", "bg-")} />
            {t}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-border/30">
        <span className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1">
          {locked && <Lock className="h-2.5 w-2.5" />}
          {locked ? "Sign in to run" : "Ready"}
        </span>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      </div>
    </div>
  );

  return locked ? (
    <button type="button" onClick={onAuth} className="text-left w-full h-full">{inner}</button>
  ) : (
    <Link to={mod.to} className="block h-full">{inner}</Link>
  );
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

export function Home() {
  const { isAuthenticated } = useSession();
  const [authOpen, setAuthOpen] = useState(false);

  const totalTechniques = useMemo(() => LAB_MODULES.reduce((sum, m) => sum + m.techniques, 0), []);
  const totalTargets = useMemo(() => LAB_MODULES.reduce((sum, m) => sum + m.targets.length, 0), []);

  const openAuth = () => setAuthOpen(true);

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="mx-auto max-w-6xl space-y-24 pb-16"
    >
      {/* ─── Hero ─────────────────────────────────────────────── */}
      <motion.section variants={fadeUp} className="relative pt-8 md:pt-16">
        {/* Animated grid backdrop */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[460px] overflow-hidden opacity-40" aria-hidden>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "linear-gradient(hsl(var(--primary)/0.15) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)/0.15) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse 70% 50% at 50% 0%, black 40%, transparent 80%)",
              WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 0%, black 40%, transparent 80%)",
            }}
          />
        </div>

        <div className="text-center space-y-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border/50 bg-card/40 backdrop-blur">
            <BrandMark className="h-4 w-4" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Hideaway · AI red-team arsenal</span>
          </div>

          <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl mx-auto">
            <span className="text-foreground"><HeroTypewriter /></span>
            <br />
            <span className="text-gradient">Hideaway finds it.</span>
          </h1>

          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            A self-serve red-team arsenal for image generation, video generation, and vision-language models.
            <span className="text-foreground"> Bring your own keys.</span> Run 14 prompt-engineering techniques, watch bypasses stream in, ship safety reports in minutes.
          </p>

          <div className="flex flex-wrap gap-3 justify-center pt-2">
            {!isAuthenticated ? (
              <Button size="lg" onClick={openAuth} className="gap-2 h-12 px-6">
                <Zap className="w-4 h-4" /> Start your first scan
              </Button>
            ) : (
              <Button asChild size="lg" className="gap-2 h-12 px-6">
                <Link to="/labs"><FlaskConical className="w-4 h-4" /> Open the Labs</Link>
              </Button>
            )}
            <Button asChild size="lg" variant="outline" className="h-12 px-6">
              <Link to="/learn">Read the techniques <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>

          {/* Trust strip */}
          <div className="pt-8 flex flex-wrap justify-center gap-x-8 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span>· Open Source · AGPL-3.0</span>
            <span>· BYOK — your keys, your spend</span>
            <span>· No model training on your data</span>
          </div>
        </div>
      </motion.section>

      {/* ─── Live preview ─────────────────────────────────────── */}
      <motion.section variants={fadeUp} className="space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-1.5 inline-flex items-center gap-1.5">
              <Terminal className="h-3 w-3" /> Live preview
            </div>
            <h2 className="font-display font-bold text-2xl md:text-3xl">Streaming attack results</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            Real product UI. Each row is one generation: technique, model, bypass score, cost, elapsed.
          </p>
        </div>
        <LiveAttackPreview />
      </motion.section>

      {/* ─── Metrics ──────────────────────────────────────────── */}
      <motion.section variants={fadeUp}>
        <div className="rounded-2xl border border-border/40 bg-gradient-to-br from-primary/5 to-transparent p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-3">
            <MetricCounter value={totalTechniques} label="Attack techniques" />
            <MetricCounter value={totalTargets} label="Target models" />
            <MetricCounter value={6} label="Safety categories" />
            <MetricCounter value={47} label="Curated probe prompts" />
          </div>
        </div>
      </motion.section>

      {/* ─── Lab modules ──────────────────────────────────────── */}
      <motion.section variants={fadeUp} className="space-y-6">
        <div className="space-y-2 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary inline-flex items-center gap-1.5">
            <Layers className="h-3 w-3" /> Three labs, one workflow
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            <PixelDecode className="">Surface area for AI red-teamers.</PixelDecode>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Each lab speaks the same shape: pick a model, pick a technique, watch results stream in. Compare 2-4 models side-by-side.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LAB_MODULES.map((mod) => (
            <motion.div key={mod.id} variants={fadeUp}>
              <LabModuleCard mod={mod} locked={!isAuthenticated} onAuth={openAuth} />
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ─── Workflow / Probe Reveal Report ───────────────────── */}
      <motion.section variants={fadeUp} className="space-y-6">
        <div className="space-y-2 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">How it works</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Probe → Reveal → Report</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FLOW_STEPS.map((s) => (
            <div key={s.n} className="relative rounded-xl border border-border/40 bg-card/40 backdrop-blur-xl p-6">
              <div className="font-mono text-[10px] text-primary tracking-wider absolute top-4 right-5 opacity-60">{s.n}</div>
              <h3 className="font-display font-bold text-xl mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ─── Differentiator strip ─────────────────────────────── */}
      <motion.section variants={fadeUp}>
        <div className="relative rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-500/5 p-6 md:p-8 overflow-hidden">
          <div className="absolute -top-12 -right-12 opacity-10 pointer-events-none" aria-hidden>
            <Eye className="h-48 w-48 text-amber-400" />
          </div>
          <div className="relative space-y-3 max-w-2xl">
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px]">
              <Sparkles className="h-3 w-3 mr-1" /> WHAT NO OTHER TOOL DOES
            </Badge>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
              Steganography × Vision Language Models
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              Hideaway's stego engine encodes instructions invisibly into pixel LSBs, rasterises them as on-image typography (FigStep), or smuggles them through PNG metadata chunks. Then the VLM Lab probes whether GPT-4V, Claude Vision, or Gemini Vision will follow what their own safety filter never saw.
            </p>
            <p className="font-mono text-[11px] text-amber-300/80">
              The same `hideaway/core.py` LSB engine that powers our free stego toolkit also powers the VLM attack pipeline.
            </p>
            <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200 transition-colors pt-1">
              Read the deep-dive <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ─── Stego Arsenal ────────────────────────────────────── */}
      <motion.section variants={fadeUp} className="space-y-6">
        <div className="space-y-2 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary inline-flex items-center gap-1.5">
            <Shield className="h-3 w-3" /> Free forever
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Stego Arsenal</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            The toolkit that started it all. Hide, reveal, scan, and analyse covert channels in any image. No sign-up required, AGPL-3.0, no per-day cap.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STEGO_TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className="group rounded-xl border border-border/40 bg-card/40 backdrop-blur-xl p-4 transition-all hover:-translate-y-0.5 hover:border-border/80"
              >
                <Icon className="h-4 w-4 text-primary mb-2 transition-transform group-hover:scale-110" />
                <div className="font-display font-bold text-sm mb-1">{t.title}</div>
                <p className="text-[11px] text-muted-foreground leading-snug">{t.description}</p>
              </Link>
            );
          })}
        </div>
      </motion.section>

      {/* ─── Pricing ──────────────────────────────────────────── */}
      <motion.section variants={fadeUp} className="space-y-6">
        <div className="space-y-2 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Pricing</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Free to start. Pro to scale.</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Bring your own provider keys at no platform markup. Platform credits cover hosted runs when you don't want to manage keys.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PRICING.map((p) => (
            <div
              key={p.name}
              className={
                "rounded-2xl border bg-card/60 backdrop-blur-xl p-6 space-y-4 transition-all " +
                (p.featured ? "border-primary/50 shadow-[0_0_40px_-12px_hsl(var(--primary)/0.4)] -translate-y-1" : "border-border/40 hover:border-border")
              }
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-lg">{p.name}</h3>
                {p.featured && <Badge className="text-[10px]"><Sparkles className="h-2.5 w-2.5 mr-1" /> Popular</Badge>}
              </div>
              <div className="font-display font-extrabold text-3xl">{p.price}</div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                variant={p.featured ? "default" : "outline"}
                className="w-full"
                onClick={() => !isAuthenticated && openAuth()}
                asChild={isAuthenticated}
              >
                {isAuthenticated ? <Link to="/labs">Open Labs</Link> : <span>{p.cta}</span>}
              </Button>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ─── Final CTA + Cryptex ──────────────────────────────── */}
      <motion.section variants={fadeUp} className="space-y-4">
        <div className="rounded-2xl border border-border/40 bg-card/60 backdrop-blur-xl p-8 md:p-12 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
            <Sparkles className="h-3 w-3 text-primary" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Ready to ship a safety report?</span>
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl mx-auto">
            Find the cracks before someone else does.
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Universally free for anyone with provider keys — no daily cap, no paywall. Add a key, run a scan, get a JSON + Markdown report you can paste into a disclosure email today.
          </p>
          <div className="pt-2 flex flex-wrap gap-3 justify-center">
            {!isAuthenticated ? (
              <Button size="lg" onClick={openAuth} className="gap-2 h-12 px-6 glow">
                <KeyRound className="w-4 h-4" /> Create account
              </Button>
            ) : (
              <Button asChild size="lg" className="gap-2 h-12 px-6 glow">
                <Link to="/providers"><KeyRound className="w-4 h-4" /> Add a provider key</Link>
              </Button>
            )}
          </div>
        </div>

        <a
          href="https://cryptex.m4xx.cfd?ref=hideaway"
          target="_blank"
          rel="noreferrer"
          className="group flex items-center gap-4 rounded-xl border border-border/40 bg-card/40 backdrop-blur-xl p-4 transition-all hover:-translate-y-0.5 hover:border-border/80"
        >
          <div className="rounded-lg bg-accent/10 p-2.5 border border-accent/20 shrink-0">
            <ExternalLink className="h-4 w-4 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              Doing text-LLM red-teaming? <span className="text-accent">Try Cryptex →</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Sister product for text-based LLM testing. Separate codebase, separate scope.
            </p>
          </div>
          <ArrowUpRight className="h-4 w-4 text-accent opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all shrink-0" />
        </a>
      </motion.section>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </motion.div>
  );
}
